import { validatorRegistry } from "./validatorRegistry";
import { valid } from "baseui/payment-card";

validatorRegistry.registerFieldValidator(
  "RequiredValidator",
  (subject, { field, options }) => {
    const message = options?.message ?? "This field is required";
    // Always include undefined as an empty value as this is what is used initially
    const emptyValues = [...(options?.emptyValues ?? [null, ""]), undefined];
    return subject.test("required", message, (value) => !emptyValues.includes(value));
  }
);

validatorRegistry.registerFieldValidator("EmailValidator", (subject) =>
  subject.email()
);

/*
 * This FE validator is only to validate the street no and show the error message
 * on change in the address search component.
 * */
validatorRegistry.registerFieldValidator("AddressValidator", (subject, { options }) => {
  const message = options?.message ?? "Your address must include a street number.";
  return subject.test("address", message, (value) => {
    if (value === undefined) {
      return true;
    }
    if (!value?.addressComponents) {
      return false;
    }

    return (
      value?.addressComponents.filter((item) => {
        return item?.types.includes("street_number");
      }).length > 0
    );
  });
});

validatorRegistry.registerFieldValidator("ExpiryValidator", (subject, { options }) => {
  const message = options?.message ?? "Expiry must be in MM/YY format";
  return subject.test("expiry", message, (value) => {
    return Boolean(value && valid.expirationDate(value).isValid);
  });
});

validatorRegistry.registerFieldValidator(
  "MinWordsValidator",
  (subject, { options }) => {
    const minWords = options?.min || 0;
    return subject.matches(`\\s*(\\w+)(\\s+\\w+){${minWords - 1},}\\s*`, {
      message: `Must be at least ${minWords} words`,
      excludeEmptyString: true,
    });
  }
);

validatorRegistry.registerFieldValidator("MinValidator", (subject, { options }) => {
  const minValue = options?.min || 0;
  return subject.min(minValue, `Must be at least ${minValue}`);
});

validatorRegistry.registerFieldValidator(
  "PastDateValidator",
  (subject, { options }) => {
    const years = options?.years;
    const maxDate = options?.maxDate ? new Date(options?.maxDate) : new Date();
    if (years) {
      maxDate.setFullYear(maxDate.getFullYear() + years);
    }
    return subject.max(maxDate, `Must be on or before ${maxDate.toLocaleDateString()}`);
  }
);

validatorRegistry.registerFieldValidator(
  "DateRangeValidator",
  (subject, { options }) => {
    let date = options.givenRange?.date
      ? new Date(options.givenRange?.date)
      : new Date();

    const time_fields = [
      "hour",
      "hours",
      "minute",
      "minutes",
      "second",
      "seconds",
      "microsecond",
      "microseconds",
    ];

    if (!time_fields.some((item) => Object.keys(options.givenRange).includes(item))) {
      date = new Date(date.toDateString());
    }

    if (options.givenRange?.years) {
      date.setFullYear(date.getFullYear() + options.givenRange?.years);
    }
    if (options.givenRange?.days) {
      date.setDate(date.getDate() + options.givenRange?.days);
    }

    if (options?.limitType === "max") {
      return subject.max(date, `Must be on or before ${date.toLocaleDateString()}`);
    } else {
      return subject.min(date, `Must be on or after ${date.toLocaleDateString()}`);
    }
  }
);

validatorRegistry.registerFieldValidator(
  "MaxCharactersValidator",
  (subject, { options }) => {
    const maxCharacters = options?.max ?? Number.MAX_SAFE_INTEGER;
    return subject.max(maxCharacters, `Must be ${maxCharacters} characters or less`);
  }
);

validatorRegistry.registerFieldValidator("AbnValidator", (subject, { options }) => {
  const message = options?.message ?? "Invalid ABN";
  return subject.test("abn", message, (value) => {
    if (value === undefined) {
      return true;
    }
    const digits = String(value)
      .replace(/\s/g, "")
      .split("")
      .map((digit) => parseInt(digit));

    if (digits.length !== 11) {
      return false;
    }
    digits[0] -= 1;
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const weightedSum = digits.reduce(
      (sum, digit, index) => sum + digit * weights[index],
      0
    );
    const remainder = weightedSum % 89;
    return remainder === 0;
  });
});

validatorRegistry.registerFieldValidator("RegexValidator", (subject, { options }) => {
  const message = options?.message ?? "The value you entered is invalid";
  return subject.matches(options?.pattern, {
    message,
    excludeEmptyString: true,
  });
});
